import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled, { css, keyframes } from "styled-components";

const PURPLE = "#5a21d2";
const PINK = "#f72586";

const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(-50%) translateX(-6px);
	}
	to {
		opacity: 1;
		transform: translateY(-50%) translateX(0);
	}
`;

const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(6px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

type IntroPlacement = "right" | "top";

const CARD_WIDTH = 320;
const VIEWPORT_MARGIN = 16;
const ANCHOR_GAP = 14;

const Card = styled.div<{ $placement: IntroPlacement; $arrowLeft: number }>`
	position: fixed;
	width: ${CARD_WIDTH}px;
	max-width: min(${CARD_WIDTH}px, calc(100vw - 120px));
	padding: 20px;
	border-radius: 20px;
	background: ${PURPLE};
	box-shadow: 0 16px 40px rgba(42, 5, 127, 0.32);
	z-index: 3200;
	animation: ${fadeIn} 220ms ease-out;
	pointer-events: auto;
	transform: translateY(-50%);

	&::before {
		content: "";
		position: absolute;
		left: -8px;
		top: 50%;
		transform: translateY(-50%);
		width: 0;
		height: 0;
		border-top: 9px solid transparent;
		border-bottom: 9px solid transparent;
		border-right: 9px solid ${PURPLE};
	}

	/* Above a bottom-bar anchor: the arrow points down at the bell. */
	${({ $placement, $arrowLeft }) =>
		$placement === "top" &&
		css`
			max-width: calc(100vw - ${VIEWPORT_MARGIN * 2}px);
			transform: none;
			animation-name: ${fadeInUp};

			&::before {
				left: ${$arrowLeft}px;
				top: auto;
				bottom: -8px;
				transform: translateX(-50%);
				border-top: 9px solid ${PURPLE};
				border-right: 9px solid transparent;
				border-bottom: 0;
				border-left: 9px solid transparent;
			}
		`}
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 10px;
`;

const Title = styled.h2`
	margin: 0;
	font-family: Poppins, sans-serif;
	font-size: 16px;
	font-weight: 700;
	line-height: 1.3;
	color: #ffffff;
`;

const NewBadge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 20px;
	padding: 0 8px;
	border-radius: 999px;
	background: ${PINK};
	color: #ffffff;
	font-family: Poppins, sans-serif;
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	flex-shrink: 0;
`;

const Body = styled.p`
	margin: 0 0 18px;
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 400;
	line-height: 1.45;
	color: #ffffff;
`;

const GotItButton = styled.button`
	border: none;
	border-radius: 999px;
	background: #ffffff;
	color: ${PURPLE};
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 700;
	line-height: 1;
	padding: 10px 18px;
	cursor: pointer;

	&:hover {
		background: #f6f4ff;
	}

	&:focus-visible {
		outline: 2px solid #ffffff;
		outline-offset: 2px;
	}
`;

interface NotificationFeatureIntroProps {
	anchorEl: HTMLElement | null;
	/** Side of the anchor to open on: "right" of the desktop rail, or "top" of the phone bottom bar. */
	placement?: IntroPlacement;
	onDismiss: () => void;
}

interface Coords {
	top?: number;
	bottom?: number;
	left: number;
	arrowLeft: number;
}

export function NotificationFeatureIntro({ anchorEl, placement = "right", onDismiss }: NotificationFeatureIntroProps) {
	const [coords, setCoords] = useState<Coords | null>(null);

	useEffect(() => {
		if (!anchorEl) {
			setCoords(null);
			return;
		}

		const update = () => {
			const rect = anchorEl.getBoundingClientRect();
			if (placement === "top") {
				const width = Math.min(CARD_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
				const anchorCenter = rect.left + rect.width / 2;
				const left = Math.min(
					Math.max(anchorCenter - width / 2, VIEWPORT_MARGIN),
					window.innerWidth - width - VIEWPORT_MARGIN,
				);
				setCoords({ bottom: window.innerHeight - rect.top + ANCHOR_GAP, left, arrowLeft: anchorCenter - left });
				return;
			}
			setCoords({
				top: rect.top + rect.height / 2,
				left: rect.right + ANCHOR_GAP,
				arrowLeft: 0,
			});
		};

		update();
		window.addEventListener("resize", update);
		window.addEventListener("scroll", update, true);
		return () => {
			window.removeEventListener("resize", update);
			window.removeEventListener("scroll", update, true);
		};
	}, [anchorEl, placement]);

	if (typeof document === "undefined" || !anchorEl || !coords) return null;

	return createPortal(
		<Card
			role="dialog"
			aria-labelledby="notification-feature-intro-title"
			aria-describedby="notification-feature-intro-body"
			$placement={placement}
			$arrowLeft={coords.arrowLeft}
			style={{ top: coords.top, bottom: coords.bottom, left: coords.left }}
		>
			<Header>
				<Title id="notification-feature-intro-title">Notification System</Title>
				<NewBadge>NEW</NewBadge>
			</Header>
			<Body id="notification-feature-intro-body">
				Keep users informed in real time with smart, customizable notifications that deliver timely updates
				in-app—so nothing important gets missed.
			</Body>
			<GotItButton type="button" onClick={onDismiss}>
				Got it!
			</GotItButton>
		</Card>,
		document.body,
	);
}
